import {useEffect,useRef,useState} from 'react';

type MaskedCard={id:string;brand:string;last4:string;expMonth:number;expYear:number};
type Intent={orderId:string;clientSecret:string;publishableKey:string;
  amountCents:number;paymentStatus:string};
type StripeCardElement={mount(node:HTMLElement):void;destroy():void};
type StripeClient={elements():{create(type:'card',options?:{disableLink:boolean}):StripeCardElement};
  confirmCardPayment(secret:string,details?:{
    payment_method:string|{card:StripeCardElement;
      billing_details:{name:string}};
  }):Promise<{error?:{message?:string};paymentIntent?:{status:string}}>;
};
type SaleLine={serviceId:string;quantity:number;packageId?:string;
  appointmentId?:string|null};
const dollar=(cents:number)=>new Intl.NumberFormat('en-US',
  {style:'currency',currency:'USD'}).format(cents/100);

/** Staff Point of Sale: source-backed Stripe TEST Key and existing cards.
 * Stripe.js owns all card fields. Only the signed webhook settles the sale.
 * Source: FloatHelm Usable Cards / Terminal / Key screenshots (Nov 3).
 */
export function StaffTicketCardPayment({location,customerId,ticketKey,eligible,
  totalCents,lines,discount,saleSnapshot,settled,onStarted,onBusyChange}:{
  location:string;customerId:string|null;ticketKey:string;eligible:boolean;
  totalCents:number;lines:SaleLine[];
  discount:{serviceId:string;discountId:string}|null;
  saleSnapshot:string;settled:boolean;
  onStarted:(orderId:string,snapshot:string)=>void;
  onBusyChange:(busy:boolean)=>void;
}){
  const [cards,setCards]=useState<MaskedCard[]>([]);
  const [cardsError,setCardsError]=useState('');
  const [mode,setMode]=useState<'key'|'saved'|null>(null);
  const [intent,setIntent]=useState<Intent|null>(null);
  const [busy,setBusy]=useState(false);
  const [ready,setReady]=useState(false);
  const [cardholder,setCardholder]=useState('');
  const [error,setError]=useState('');
  const [status,setStatus]=useState('');
  const [accepted,setAccepted]=useState(false);
  const [saveForFuture,setSaveForFuture]=useState(false);
  const cardNode=useRef<HTMLDivElement|null>(null);
  const stripeCard=useRef<{stripe:StripeClient;card:StripeCardElement}|null>(null);
  useEffect(()=>{
    setCards([]);setCardsError('');setMode(null);setIntent(null);
    setReady(false);setCardholder('');setStatus('');setError('');
    setAccepted(false);setSaveForFuture(false);
  },[location,customerId,ticketKey]);
  // Item/discount edits invalidate the payment form, not the selected
  // person's already-fetched saved-card list. Preserve real masked cards.
  useEffect(()=>{
    setMode(null);setIntent(null);setReady(false);
    setStatus('');setError('');setAccepted(false);
  },[saleSnapshot]);
  useEffect(()=>{
    if(!customerId)return;
    const controller=new AbortController();
    void fetch('/api/v1/staff/customers/'+encodeURIComponent(customerId)+
      '/cards/test?'+new URLSearchParams({location}),{
        credentials:'same-origin',cache:'no-store',signal:controller.signal,
      }).then(async response=>{
      const body=await response.json() as {ok:boolean;data?:{cards:MaskedCard[]}};
      if(!response.ok||!body.ok||!Array.isArray(body.data?.cards))
        throw Error('Saved cards are unavailable.');
      if(!controller.signal.aborted)setCards(body.data.cards);
    }).catch(()=>{
      if(!controller.signal.aborted)setCardsError('Saved cards unavailable.');
    });
    return ()=>controller.abort();
  },[customerId,location]);
  useEffect(()=>{onBusyChange(busy);},[busy,onBusyChange]);
  useEffect(()=>{
    if(mode!=='key'||!intent||!cardNode.current)return;
    if(!window.Stripe){setError('Stripe secure card fields are unavailable.');return;}
    const stripe=window.Stripe(intent.publishableKey) as unknown as StripeClient;
    const card=stripe.elements().create('card',{disableLink:true});
    card.mount(cardNode.current);
    stripeCard.current={stripe,card};setReady(true);
    return ()=>{card.destroy();stripeCard.current=null;setReady(false);};
  },[mode,intent]);
  const create=async(savedCardId?:string):Promise<Intent>=>{
    if(!customerId)throw Error('Select a customer to charge.');
    const bodyRequest={location,customerId,idempotencyKey:ticketKey,
      ...(discount?{serviceId:discount.serviceId,
        discountId:discount.discountId}:
        {lines:lines.map(line=>({
          serviceId:line.serviceId,quantity:line.quantity,
          ...(line.packageId?{packageId:line.packageId}:{}),
          ...(line.appointmentId?{appointmentId:line.appointmentId}:{})
        }))}),
      ...(savedCardId?{savedPaymentMethodId:savedCardId}:{}),
      ...(!savedCardId&&saveForFuture?{saveForFuture:true}:{}),
    };
    const response=await fetch('/api/v1/staff/sales/stripe/test-inline-intent',{
      method:'POST',credentials:'same-origin',cache:'no-store',
      headers:{'content-type':'application/json'},body:JSON.stringify(bodyRequest),
    });
    const body=await response.json() as {ok:boolean;data?:Intent;error?:{code?:string}};
    if(!response.ok||!body.ok||!body.data?.clientSecret||
      !body.data.publishableKey?.startsWith('pk_test_')||
      body.data.paymentStatus!=='UNPAID_TEST_INTENT'||
      body.data.amountCents!==totalCents)
      throw Error(body.error?.code==='STAFF_TEST_CARD_NOT_ON_CUSTOMER'?
        'That saved card does not belong to the selected customer.':
        body.error?.code==='STAFF_TEST_INLINE_ORDER_CONFLICT'?
          'This ticket already has a different payment pending.':
          body.error?.code??'Secure Stripe TEST payment unavailable.');
    return body.data;
  };
  const beginKey=async()=>{
    if(!eligible||busy||settled||accepted)return;
    setBusy(true);setError('');setStatus('');setMode('key');setReady(false);
    try{
      const next=await create();
      setIntent(next);onStarted(next.orderId,saleSnapshot);
    }catch(e){
      setMode(null);setIntent(null);
      setError(e instanceof Error?e.message:'Could not prepare Key payment.');
    }finally{setBusy(false);}
  };
  const confirmKey=async()=>{
    if(!intent||!stripeCard.current||!ready||busy||settled||accepted)return;
    setBusy(true);setError('');setStatus('');
    try{
      const {stripe,card}=stripeCard.current;
      const result=await stripe.confirmCardPayment(intent.clientSecret,{
        payment_method:{card,billing_details:{name:cardholder.trim()}},
      });
      if(result.error||result.paymentIntent?.status!=='succeeded')
        throw Error(result.error?.message??'Stripe did not complete this payment.');
      setAccepted(true);
      setStatus('Stripe accepted the TEST card. Verifying signed payment and credits…');
    }catch(e){
      setError(e instanceof Error?e.message:'Unable to charge card.');
    }finally{setBusy(false);}
  };
  const paySaved=async(card:MaskedCard)=>{
    if(!eligible||busy||settled||accepted)return;
    setBusy(true);setError('');setStatus('');setMode('saved');setIntent(null);
    try{
      const next=await create(card.id);
      setIntent(next);onStarted(next.orderId,saleSnapshot);
      if(!window.Stripe)throw Error('Stripe secure payment is unavailable.');
      const stripe=window.Stripe(next.publishableKey) as unknown as StripeClient;
      const result=await stripe.confirmCardPayment(next.clientSecret,{
        payment_method:card.id,
      });
      if(result.error||result.paymentIntent?.status!=='succeeded')
        throw Error(result.error?.message??'Stripe did not complete this payment.');
      setAccepted(true);
      setStatus('Stripe accepted the saved TEST card. Verifying signed payment and credits…');
    }catch(e){
      setError(e instanceof Error?e.message:'Unable to use saved card.');
    }finally{setBusy(false);}
  };
  return <div className="ecfs-staff-card-tender" aria-label="Card payment options">
    <div className="ecfs-sale-payment-methods">
      <button type="button" disabled title="No Stripe Terminal reader is connected">
        Terminal</button>
      <button type="button" disabled={!eligible||busy||settled||accepted}
        onClick={()=>void beginKey()}>Key</button>
    </div>
    <label className="ecfs-staff-card-save-option">
      <input type="checkbox" checked={saveForFuture}
        disabled={!eligible||busy||settled||accepted||mode!==null}
        onChange={e=>setSaveForFuture(e.target.checked)}/>
      Save a newly keyed card to this customer's Usable Cards for future purchases
    </label>
    {cards.length>0&&<div className="ecfs-sale-payment-methods ecfs-staff-saved-cards"
      aria-label="Stored cards">
      {cards.map(card=><button type="button" key={card.id}
        disabled={!eligible||busy||settled||accepted}
        aria-label={'Pay with '+card.brand+' ending '+card.last4}
        onClick={()=>void paySaved(card)}>
        {card.brand} •••• {card.last4}</button>)}
    </div>}
    {cardsError&&<small role="status">{cardsError}</small>}
    {mode==='key'&&<section className="ecfs-staff-key-fields"
      aria-label="Key card payment">
      <strong>Key Card · Stripe TEST · {dollar(totalCents)}</strong>
      <label>Name on Card
        <input value={cardholder} onChange={e=>setCardholder(e.target.value)}
          autoComplete="cc-name"/></label>
      <label>Card Number, Expiration and CVC
        <div ref={cardNode} className="ecfs-staff-stripe-fields"
          aria-label="Secure Stripe card fields"/></label>
      <div className="ecfs-staff-key-actions">
        <button type="button" disabled={busy||!ready||settled||accepted}
          onClick={()=>void confirmKey()}>
          {busy?'Processing TEST card…':'Charge '+dollar(totalCents)}</button>
        <button type="button" disabled={busy||accepted} onClick={()=>{
          setMode(null);setIntent(null);setError('');setStatus('');
        }}>Cancel</button>
      </div>
    </section>}
    {status&&<small role="status">{status}</small>}
    {error&&<small role="alert">{error}</small>}
  </div>;
}
